/**
 * ToR Extraction for CV Matching Service
 * 
 * Extracts consultant requirements from ToR documents for CV matching
 */

import axios, { AxiosError } from 'axios';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_API_URL || '/api';

// TypeScript interfaces

/** Tier levels for requirement importance */
export type RequirementTier = 'neutral' | 'required' | 'preferred' | 'bonus';

/** Single requirement item with its tier */
export interface RequirementItem {
    text: string;
    tier: RequirementTier;
}

/** Category containing items with individual tiers */
export interface RequirementCategory {
    items: RequirementItem[];
}

/** Geographic category with regions and countries */
export interface GeographicRequirements {
    regions: RequirementItem[];
    countries: RequirementItem[];
}

/** Experience requirements with years and types */
export interface ExperienceRequirements {
    minimum_years: number | null;
    minimum_years_tier: RequirementTier;
    experience_types: RequirementItem[];
}

/** Education requirements */
export interface EducationRequirements {
    minimum_level: string | null;
    minimum_level_tier: RequirementTier;
    fields: RequirementItem[];
}

/** Team requirements */
export interface TeamRequirements {
    role: string | null;
    level: string | null;
    team_size: number | null;
    team_composition: string | null;
}

/** Contextual requirements */
export interface ContextualRequirements {
    lmic_experience: { value: boolean; tier: RequirementTier };
    government_engagement: { value: boolean; tier: RequirementTier };
    donor_systems_knowledge: RequirementItem[];
    national_policy_familiarity: { value: boolean; tier: RequirementTier };
    fragile_conflict_states: { value: boolean; tier: RequirementTier };
}

/**
 * New granular consultant requirements structure
 * Each item has its own tier (required/preferred/bonus)
 */
export interface ConsultantRequirementsV2 {
    project_title: string;
    
    // Skill categories with per-item tiers
    methods_methodologies: RequirementCategory;
    technical_domain_expertise: RequirementCategory;
    technical_tools_software: RequirementCategory;
    frameworks_standards: RequirementCategory;
    communication_stakeholder: RequirementCategory;
    professional_ethical: RequirementCategory;
    
    // Geographic with per-item tiers
    geographic_experience: GeographicRequirements;
    
    // Language with per-item tiers
    language_requirements: RequirementCategory;
    
    // Sector with per-item tiers
    sector_experience: RequirementCategory;
    
    // Experience with granular tiers
    experience_requirements: ExperienceRequirements;
    
    // Education with granular tiers
    education_requirements: EducationRequirements;
    
    // Team requirements (no tiers needed)
    team_requirements: TeamRequirements;
    
    // Donor experience with per-item tiers
    donor_experience: RequirementCategory;
    
    // Contextual requirements with per-item tiers
    contextual_requirements: ContextualRequirements;
}

/**
 * Legacy interface for backward compatibility
 * @deprecated Use ConsultantRequirementsV2 instead
 */
export interface ConsultantRequirements {
    project_title: string;
    
    // Comprehensive skill categories (supporting both old and new standardized field names)
    research_evaluation_skills?: {
        required: string[];
        preferred: string[];
    };
    methods_methodologies?: {
        required: string[];
        preferred: string[];
    };
    
    technical_domain_expertise?: {
        required: string[];
        preferred: string[];
    };
    
    analytical_tools?: {
        required: string[];
        preferred: string[];
    };
    technical_tools_software?: {
        required: string[];
        preferred: string[];
    };
    
    frameworks_methodologies?: {
        required: string[];
        preferred: string[];
    };
    frameworks_standards?: {
        required: string[];
        preferred: string[];
    };
    
    communication_stakeholder?: {
        required: string[];
        preferred: string[];
    };
    
    professional_ethical?: {
        required: string[];
        preferred: string[];
    };
    
    // Geographic and language
    geographic_experience: {
        required_regions: string[];
        required_countries: string[];
        preferred_regions: string[];
        preferred_countries: string[];
    };
    
    language_requirements: {
        required: string[];
        preferred: string[];
    };
    
    // Sector and experience
    sector_experience: {
        required: string[];
        preferred: string[];
    };
    
    experience_requirements: {
        minimum_years: number | null;
        required_experience_types: string[];
        preferred_experience_types: string[];
    };
    
    // Education
    education_requirements: {
        minimum_level: string | null;
        required_fields?: string[];
        preferred_fields: string[];
    };
    
    // Team requirements
    team_requirements: {
        role: string;
        level: string;
        team_size: number | null;
        team_composition?: string;
    };
    
    // Donor experience
    donor_experience?: {
        required: string[];
        preferred: string[];
    };
    
    // Contextual requirements
    contextual_requirements?: {
        lmic_experience: boolean;
        government_engagement: boolean;
        donor_systems_knowledge: string[];
        national_policy_familiarity: boolean;
        fragile_conflict_states: boolean;
    };
    
    // Legacy fields for backward compatibility
    required_skills?: string[];
    preferred_skills?: string[];
    technical_expertise?: {
        required_tools: string[];
        required_methods: string[];
        preferred_tools: string[];
        preferred_methods: string[];
    };
    special_requirements?: string[];
}

/**
 * Convert legacy requirements to V2 format
 */
export function convertToV2(legacy: ConsultantRequirements): ConsultantRequirementsV2 {
    // All items default to 'neutral' - user selects which are relevant
    const toItems = (required: string[] = [], preferred: string[] = []): RequirementItem[] => [
        ...required.map(text => ({ text, tier: 'neutral' as RequirementTier })),
        ...preferred.map(text => ({ text, tier: 'neutral' as RequirementTier }))
    ];
    
    return {
        project_title: legacy.project_title,
        methods_methodologies: {
            items: toItems(
                legacy.methods_methodologies?.required || legacy.research_evaluation_skills?.required,
                legacy.methods_methodologies?.preferred || legacy.research_evaluation_skills?.preferred
            )
        },
        technical_domain_expertise: {
            items: toItems(legacy.technical_domain_expertise?.required, legacy.technical_domain_expertise?.preferred)
        },
        technical_tools_software: {
            items: toItems(
                legacy.technical_tools_software?.required || legacy.analytical_tools?.required,
                legacy.technical_tools_software?.preferred || legacy.analytical_tools?.preferred
            )
        },
        frameworks_standards: {
            items: toItems(
                legacy.frameworks_standards?.required || legacy.frameworks_methodologies?.required,
                legacy.frameworks_standards?.preferred || legacy.frameworks_methodologies?.preferred
            )
        },
        communication_stakeholder: {
            items: toItems(legacy.communication_stakeholder?.required, legacy.communication_stakeholder?.preferred)
        },
        professional_ethical: {
            items: toItems(legacy.professional_ethical?.required, legacy.professional_ethical?.preferred)
        },
        geographic_experience: {
            regions: toItems(
                legacy.geographic_experience?.required_regions,
                legacy.geographic_experience?.preferred_regions
            ),
            countries: toItems(
                legacy.geographic_experience?.required_countries,
                legacy.geographic_experience?.preferred_countries
            )
        },
        language_requirements: {
            items: toItems(legacy.language_requirements?.required, legacy.language_requirements?.preferred)
        },
        sector_experience: {
            items: toItems(legacy.sector_experience?.required, legacy.sector_experience?.preferred)
        },
        experience_requirements: {
            minimum_years: legacy.experience_requirements?.minimum_years || null,
            minimum_years_tier: 'neutral',
            experience_types: toItems(
                legacy.experience_requirements?.required_experience_types,
                legacy.experience_requirements?.preferred_experience_types
            )
        },
        education_requirements: {
            minimum_level: legacy.education_requirements?.minimum_level || null,
            minimum_level_tier: 'neutral',
            fields: toItems(
                legacy.education_requirements?.required_fields,
                legacy.education_requirements?.preferred_fields
            )
        },
        team_requirements: {
            role: legacy.team_requirements?.role || null,
            level: legacy.team_requirements?.level || null,
            team_size: legacy.team_requirements?.team_size || null,
            team_composition: legacy.team_requirements?.team_composition || null
        },
        donor_experience: {
            items: toItems(legacy.donor_experience?.required, legacy.donor_experience?.preferred)
        },
        contextual_requirements: {
            lmic_experience: { value: legacy.contextual_requirements?.lmic_experience || false, tier: 'neutral' },
            government_engagement: { value: legacy.contextual_requirements?.government_engagement || false, tier: 'neutral' },
            donor_systems_knowledge: (legacy.contextual_requirements?.donor_systems_knowledge || []).map(text => ({ text, tier: 'neutral' as RequirementTier })),
            national_policy_familiarity: { value: legacy.contextual_requirements?.national_policy_familiarity || false, tier: 'neutral' },
            fragile_conflict_states: { value: legacy.contextual_requirements?.fragile_conflict_states || false, tier: 'neutral' }
        }
    };
}

export interface TorForCVsExtractionResult {
    requirements: ConsultantRequirements;
    query_vector?: number[];
    full_tor_text?: string;
    project_title?: string;
    client_name?: string;
}

export interface TorForCVsExtractionResultV2 {
    requirements: ConsultantRequirementsV2;
    query_vector?: number[];
    full_tor_text?: string;
    project_title?: string;
    client_name?: string;
}

/**
 * Extract consultant requirements from pasted text (no PDF upload needed)
 * This sends the text directly to the extraction API
 */
export async function extractTorFromText(text: string): Promise<TorForCVsExtractionResult> {
    if (!text || text.trim().length < 50) {
        throw new Error('Please paste more text. The requirements section should be at least a few sentences.');
    }

    try {
        console.log(`Sending ${text.length} characters to Azure Function for consultant requirements extraction...`);

        const response = await axios.post<TorForCVsExtractionResult>(
            `${API_URL}/extract-tor-for-cvs`,
            { tor_text: text },
            {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true,
                timeout: 120000 // 2 minutes
            }
        );

        console.log('Consultant requirements extracted:', response.data);

        if (!response.data.query_vector) {
            console.warn('Warning: query_vector not returned. This may affect CV matching.');
        }

        return {
            ...response.data,
            full_tor_text: text
        };

    } catch (error) {
        console.error('Text extraction for CVs failed:', error);
        
        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            
            if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ERR_CONNECTION_CLOSED') {
                throw new Error('Extraction service is not available. The function may not be deployed yet.');
            }
            
            if (axiosError.response?.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            
            if (axiosError.response?.status === 500) {
                throw new Error('Extraction service error. Please try again.');
            }
        }
        
        throw new Error(error instanceof Error ? error.message : 'Failed to extract requirements from text');
    }
}

/**
 * Extract text from PDF with proper spacing
 */
async function extractTextFromPDF(file: File): Promise<string> {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const textPages: string[] = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();

            let lastY = -1;
            let pageText = '';

            for (const item of textContent.items) {
                const textItem = item as any;

                if (lastY !== -1 && Math.abs(textItem.transform[5] - lastY) > 5) {
                    pageText += '\n';
                }

                pageText += textItem.str;
                lastY = textItem.transform[5];
            }

            textPages.push(pageText);
        }

        return textPages.join('\n\n');
    } catch (error) {
        console.error('PDF extraction failed:', error);
        throw new Error('Failed to extract text from PDF. The PDF may be corrupted or image-based.');
    }
}

/**
 * Upload ToR and extract consultant requirements for CV matching
 */
export async function extractTorForCVs(file: File): Promise<TorForCVsExtractionResult> {
    // Validate file
    if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are supported.');
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB.');
    }

    try {
        console.log('Extracting text from PDF...');
        const torText = await extractTextFromPDF(file);

        if (!torText || torText.trim().length < 100) {
            throw new Error('PDF extraction failed or returned insufficient text. The PDF may be scanned/image-based or corrupted.');
        }

        console.log(`Sending ${torText.length} characters to Azure Function for consultant requirements extraction...`);

        const response = await axios.post<TorForCVsExtractionResult>(
            `${API_URL}/extract-tor-for-cvs`,
            { tor_text: torText },
            {
                headers: {
                    'Content-Type': 'application/json'
                },
                withCredentials: true,
                timeout: 120000 // 2 minutes
            }
        );

        console.log('Consultant requirements extracted:', response.data);

        if (!response.data.query_vector) {
            console.warn('Warning: query_vector not returned. This may affect CV matching.');
        }

        return {
            ...response.data,
            full_tor_text: torText
        };

    } catch (error) {
        console.error('ToR extraction for CVs failed:', error);
        
        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError;
            
            if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ERR_CONNECTION_CLOSED') {
                throw new Error('ToR extraction service is not available. The function may not be deployed yet.');
            }
            
            if (axiosError.response?.status === 401) {
                throw new Error('Authentication required. Please log in.');
            }
            
            if (axiosError.response?.status === 500) {
                throw new Error('ToR extraction service error. Please try again.');
            }
        }
        
        throw new Error(error instanceof Error ? error.message : 'Failed to extract ToR requirements');
    }
}
